import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ARTENNAME,
  type Felder,
  type Suchart,
  type Suchergebnis,
  type Treffer,
  type Verlaufseintrag,
  suchen,
  verlauf as holeVerlauf,
  verlaufLoeschen,
} from '../api/suche';
import { Schliesskreuz } from '../komponenten/Schliesskreuz';

/**
 * Das Suchfenster (M-07).
 *
 * ZWEI BETRIEBSARTEN, EIN FENSTER. Aus der Kopfleiste heraus SPRINGT es und
 * beantwortet die häufigste Frage überhaupt: Wo war ich gerade? An einem
 * Feld, das auf einen Datensatz zeigt, WÄHLT es aus und gibt den Wert
 * zurück, ohne die Maske zu verlassen. Aufbau, Tastatur und Suchverhalten
 * sind in beiden Fällen dieselben — deshalb ist es eine Komponente und nicht
 * zwei.
 *
 * ES GIBT KEIN ALLGEMEINES SUCHFELD. Das ist die tragende Festlegung: Gesucht
 * wird über benannte Felder, die zur gewählten Art gehören und sich mit UND
 * gegenseitig verengen. Ein Feld leeren erweitert wieder — das ist der Grund,
 * warum die Felder nebeneinander stehen bleiben und nicht hinter einem
 * Umschalter verschwinden.
 */

export type Betriebsart = 'springen' | 'auswaehlen';

/** Was das Fenster zurückgibt. */
export type Wahl = {
  readonly art: Suchart;
  readonly id: string;
  readonly bezeichnung: string;
};

type Feldart = 'text' | 'auswahl' | 'zeitraum' | 'betrag' | 'datum';

type Felddefinition = {
  /** Der Name, der über der Liste gezählt wird ("3 Felder gesetzt"). */
  readonly name: string;
  readonly art: Feldart;
  readonly schluessel: keyof Felder;
  readonly auswahl?: readonly { readonly wert: string; readonly text: string }[];
};

/**
 * Die benannten Felder je Art — wörtlich aus M-07: "bei Kunden Name oder
 * Nummer, Ort, Betreuer, Status; bei Belegen Kunde, Nummer, Zeitraum,
 * Betrag."
 *
 * Sie stehen als Daten und nicht als sechs Formulare: Die Felder wechseln mit
 * der Art, nicht die Maske.
 */
const FELDER: Readonly<Record<Suchart, readonly Felddefinition[]>> = {
  kunde: [
    { name: 'Name oder Nummer', art: 'text', schluessel: 'text' },
    { name: 'Ort', art: 'text', schluessel: 'ort' },
    {
      name: 'Betreuer',
      art: 'auswahl',
      schluessel: 'betreuer',
      /*
       * Nur "ich" und nicht die ganze Mannschaft: Die Benutzerliste steht
       * hinter `einstellungen/sehen`, und das hat der Vertrieb nicht. Eine
       * Auswahl, die für die halbe Belegschaft leer bleibt, wäre schlechter
       * als eine, die den häufigsten Fall trifft — die Vorlage tippt an
       * dieser Stelle selbst "ich" ins Feld.
       */
      auswahl: [{ wert: 'ich', text: 'ich' }],
    },
    {
      name: 'Status',
      art: 'auswahl',
      schluessel: 'status',
      auswahl: [
        { wert: 'interessent', text: 'Interessent' },
        { wert: 'kunde', text: 'Kunde' },
        /* PRD-38 (CRM-171): eine Rolle neben dem Status, gefiltert wie in der Kundenliste. */
        { wert: 'kostentraeger', text: 'Kostenträger' },
        { wert: 'inaktiv', text: 'inaktiv' },
        { wert: 'gesperrt', text: 'gesperrt' },
      ],
    },
  ],
  ansprechpartner: [
    { name: 'Name', art: 'text', schluessel: 'text' },
    { name: 'Kunde', art: 'text', schluessel: 'kunde' },
    { name: 'Funktion', art: 'text', schluessel: 'funktion' },
  ],
  angebot: belegfelder(),
  auftrag: belegfelder(),
  rechnung: belegfelder(),
  /* SYS-08 (CRM-117): der Tag greift sofort, Produkt und Kunde ab drei Zeichen. */
  termin: [
    { name: 'Datum', art: 'datum', schluessel: 'datum' },
    { name: 'Produkt', art: 'text', schluessel: 'produkt' },
    { name: 'Kunde', art: 'text', schluessel: 'kunde' },
  ],
};

function belegfelder(): readonly Felddefinition[] {
  return [
    { name: 'Kunde', art: 'text', schluessel: 'kunde' },
    { name: 'Nummer', art: 'text', schluessel: 'nummer' },
    // Zeitraum und Betrag tragen je ZWEI Eingaben und zählen als EIN Feld:
    // Über der Liste steht, wonach gefragt wurde, nicht wie viele Kästen die
    // Maske dafür braucht.
    { name: 'Zeitraum', art: 'zeitraum', schluessel: 'von' },
    { name: 'Betrag', art: 'betrag', schluessel: 'betragVon' },
  ];
}

/** Der Takt, in dem getippte Zeichen zur Abfrage werden. */
const TAKT = 220;

/**
 * Ein Betrag, wie die API ihn nimmt: mit PUNKT, ohne Tausenderpunkt.
 *
 * Das Komma wird UMGESETZT und nicht abgewiesen — auf einer deutschen
 * Tastatur ist es das, was man tippt. Was danach immer noch nicht passt,
 * bleibt hier stehen: Ein "1.234,50" an die API zu schicken hiesse, aus einer
 * Eingabe am Feld einen Serverfehler zu machen (ERR-03).
 */
export function alsBetrag(eingabe: string): string | undefined {
  const gesetzt = eingabe.trim().replace(',', '.');
  return /^\d{1,10}(\.\d{1,2})?$/.test(gesetzt) ? gesetzt : undefined;
}

/** Ein Verlaufseintrag ist eine Trefferzeile — dieselbe Darstellung. */
function alsTreffer(eintrag: Verlaufseintrag): Treffer {
  return {
    art: eintrag.art,
    id: eintrag.datensatz,
    bezeichnung: eintrag.bezeichnung,
    zusatz: eintrag.zusatz ?? '',
  };
}

/** "vor 12 Min.", "vor 3 Tagen", "letzte Woche" — wie in der Vorlage. */
export function seit(zeitpunkt: string): string {
  const minuten = Math.max(0, Math.floor((Date.now() - Date.parse(zeitpunkt)) / 60_000));
  if (minuten < 1) return 'gerade eben';
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.round(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  const tage = Math.round(stunden / 24);
  if (tage === 1) return 'gestern';
  if (tage < 7) return `vor ${tage} Tagen`;
  if (tage < 14) return 'letzte Woche';
  return `vor ${Math.round(tage / 7)} Wochen`;
}

export function Suchfenster({
  betriebsart,
  arten,
  festeArt,
  beiWahl,
  beiSchliessen,
  beiAlleTreffer,
}: {
  readonly betriebsart: Betriebsart;
  /** Die Arten, die dieser Benutzer sehen darf — beim Springen die Spalte. */
  readonly arten: readonly Suchart[];
  /** Im Auswahl-Betrieb steht die Art fest und ist nicht wählbar. */
  readonly festeArt?: Suchart;
  readonly beiWahl: (wahl: Wahl) => void;
  readonly beiSchliessen: () => void;
  /** Nur beim Springen: der Weg in die volle Trefferliste. */
  readonly beiAlleTreffer?: (art: Suchart, felder: Felder) => void;
}): JSX.Element {
  const springen = betriebsart === 'springen';

  /**
   * "Alle" ist der Verlauf über alle Arten hinweg und trägt keine Suchfelder.
   * Im Auswahl-Betrieb gibt es ihn nicht: Die Art steht fest.
   */
  const [art, setzeArt] = useState<Suchart | 'alle'>(festeArt ?? (springen ? 'alle' : 'kunde'));
  const [felder, setzeFelder] = useState<Felder>({});
  const [nurEigene, setzeNurEigene] = useState(true);
  const [verlauf, setzeVerlauf] = useState<readonly Verlaufseintrag[]>([]);
  const [ergebnis, setzeErgebnis] = useState<Suchergebnis | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [markiert, setzeMarkiert] = useState(0);
  const [fehler, setzeFehler] = useState<string | undefined>(undefined);

  const fenster = useRef<HTMLDivElement>(null);
  const erstesFeld = useRef<HTMLInputElement>(null);

  /**
   * WOHIN ESC ZURÜCKFÜHRT.
   *
   * Das Element, das den Fokus hatte, als das Fenster aufging. "Esc schließt
   * und gibt den Fokus an die Maske zurück, aus der man kam. Nichts geht
   * dabei verloren." Ohne dieses Merken landet der Fokus am Seitenanfang, und
   * der Benutzer tabbt sich durch die halbe Navigation zurück.
   */
  const herkunft = useRef<HTMLElement | null>(null);
  useEffect(() => {
    herkunft.current = document.activeElement as HTMLElement | null;
    return () => herkunft.current?.focus();
  }, []);

  /*
   * DER FOKUS MUSS IM FENSTER LIEGEN — auch dann, wenn es gar kein Feld gibt.
   *
   * Bei "Alle" traegt das Fenster keine Suchfelder (M-07). Ohne diesen Griff
   * bleibt der Fokus draussen, der Tastenfaenger sitzt an einem Element, das
   * nie ein Ereignis sieht, und ↑ ↓ ⏎ Esc tun nichts — ausgerechnet im
   * Zustand, in dem das Fenster aufgeht.
   *
   * Er greift auch beim Wechsel der Art: Wer links eine Art waehlt, will
   * anschliessend tippen und nicht erst tabben.
   */
  useEffect(() => {
    if (erstesFeld.current !== null) erstesFeld.current.focus();
    else fenster.current?.focus();
  }, [art]);

  /* Der Verlauf steht sofort — er ist der Grund, warum das Fenster ohne
     Eingabe etwas zeigt (M-07: "Fokus genügt"). */
  useEffect(() => {
    void holeVerlauf()
      .then(setzeVerlauf)
      .catch(() => setzeVerlauf([]));
  }, []);

  const felderDerArt = art === 'alle' ? [] : (FELDER[art] ?? []);

  /**
   * Ob überhaupt ein Feld greift.
   *
   * Textfelder ab drei Zeichen (KUN-22), Auswahl- und Zeitraumfelder sofort.
   * Solange keines greift, steht der Verlauf — er soll nicht flackern, nur
   * weil jemand zwei Buchstaben getippt hat.
   */
  const { greift, gesendet, unlesbar } = useMemo(() => {
    const gesendet: Record<string, string> = {};
    const unlesbar: string[] = [];
    let greift = false;

    for (const feld of felderDerArt) {
      if (feld.art === 'zeitraum') {
        for (const schluessel of ['von', 'bis'] as const) {
          const wert = (felder[schluessel] ?? '').trim();
          if (wert === '') continue;
          gesendet[schluessel] = wert;
          greift = true;
        }
        continue;
      }
      if (feld.art === 'betrag') {
        for (const schluessel of ['betragVon', 'betragBis'] as const) {
          const roh = (felder[schluessel] ?? '').trim();
          if (roh === '') continue;
          const wert = alsBetrag(roh);
          if (wert === undefined) {
            if (!unlesbar.includes(feld.name)) unlesbar.push(feld.name);
            continue;
          }
          gesendet[schluessel] = wert;
          greift = true;
        }
        continue;
      }
      const wert = (felder[feld.schluessel] ?? '').trim();
      if (wert === '') continue;
      if (feld.art === 'auswahl' || feld.art === 'datum' || wert.length >= 3) {
        gesendet[feld.schluessel] = wert;
        greift = true;
      }
    }
    return { greift, gesendet: gesendet as Felder, unlesbar };
  }, [felderDerArt, felder]);

  /* Die Suche. Getaktet, damit nicht jeder Anschlag eine Abfrage auslöst. */
  useEffect(() => {
    if (art === 'alle' || !greift) {
      setzeErgebnis(undefined);
      setzeLaeuft(false);
      return;
    }
    setzeLaeuft(true);
    let verworfen = false;
    const uhr = setTimeout(() => {
      void suchen(art, gesendet, nurEigene)
        .then((e) => {
          if (verworfen) return;
          setzeErgebnis(e);
          setzeMarkiert(0);
          setzeFehler(undefined);
        })
        .catch(() => {
          if (!verworfen) setzeFehler('Die Suche ist nicht durchgekommen.');
        })
        .finally(() => {
          if (!verworfen) setzeLaeuft(false);
        });
    }, TAKT);
    return () => {
      verworfen = true;
      clearTimeout(uhr);
    };
  }, [art, gesendet, nurEigene, greift]);

  /**
   * Was die Liste zeigt: Treffer, wenn ein Feld greift — sonst der Verlauf
   * der gewählten Art. Bei "Alle" der Verlauf über alle Arten.
   */
  const zeilen: readonly (Treffer & { readonly wann?: string })[] = useMemo(() => {
    if (ergebnis !== undefined && greift) return ergebnis.treffer;
    const gefiltert = art === 'alle' ? verlauf : verlauf.filter((e) => e.art === art);
    return gefiltert.map((e) => ({ ...alsTreffer(e), wann: seit(e.gesehenAm) }));
  }, [ergebnis, greift, verlauf, art]);

  const zeigtVerlauf = !(ergebnis !== undefined && greift);

  const waehle = useCallback(
    (zeile: Treffer): void => {
      beiWahl({ art: zeile.art, id: zeile.id, bezeichnung: zeile.bezeichnung });
    },
    [beiWahl],
  );

  /**
   * DIE TASTATUR FÜHRT ALLEIN.
   *
   * Der Fänger sitzt am Fenster und nicht am Dokument: Ein globaler Fänger
   * würde auch dann greifen, wenn das Fenster gar nicht offen ist, und ↑ ↓
   * in der dahinterliegenden Maske abfangen.
   */
  const beiTaste = (ereignis: React.KeyboardEvent<HTMLDivElement>): void => {
    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      beiSchliessen();
      return;
    }
    if (ereignis.key === 'ArrowDown') {
      ereignis.preventDefault();
      setzeMarkiert((i) => Math.min(i + 1, Math.max(0, zeilen.length - 1)));
      return;
    }
    if (ereignis.key === 'ArrowUp') {
      ereignis.preventDefault();
      setzeMarkiert((i) => Math.max(0, i - 1));
      return;
    }
    if (ereignis.key === 'Enter') {
      const zeile = zeilen[markiert];
      if (zeile !== undefined) {
        ereignis.preventDefault();
        waehle(zeile);
      }
    }
  };

  const setze = (schluessel: keyof Felder, wert: string): void =>
    setzeFelder((bisher) => ({ ...bisher, [schluessel]: wert }));

  /** Die Zählung über der Liste — sie beantwortet "warum ist die Liste kurz?". */
  const zaehlung = ((): string => {
    if (art === 'alle') return `${verlauf.length} zuletzt geöffnet`;
    if (!greift) {
      const eigene = verlauf.filter((e) => e.art === art).length;
      return `${eigene} zuletzt geöffnet`;
    }
    if (ergebnis === undefined) return 'wird gesucht …';
    const felderWort = ergebnis.felderGesetzt === 1 ? '1 Feld gesetzt' : `${ergebnis.felderGesetzt} Felder gesetzt`;
    const zahl = ergebnis.anzahlGenau ? `${ergebnis.anzahl}` : `mehr als ${ergebnis.anzahl}`;
    const trefferWort = ergebnis.anzahl === 1 && ergebnis.anzahlGenau ? '1 Treffer' : `${zahl} Treffer`;
    return `${felderWort} · ${trefferWort}`;
  })();

  return (
    <div className="suchfenster" onKeyDown={beiTaste}>
      {/* Der Schleier liegt über der ganzen Anwendung. Die Maske darunter
          bleibt sichtbar, damit man weiß, wohin Esc zurückführt. */}
      <button
        type="button"
        className="suchfenster__schleier"
        aria-label="Suche schließen"
        onClick={beiSchliessen}
      />

      {/*
        tabIndex={-1} traegt den Fokus, solange kein Feld ihn hat — bei "Alle"
        gibt es keines. Er sitzt am DIALOG und nicht am Schleier darum: Der
        Fokus soll innerhalb dessen liegen, was aria-modal umschliesst.
      */}
      <div
        className="suchfenster__fenster"
        role="dialog"
        aria-modal="true"
        aria-labelledby="suchfenster-titel"
        ref={fenster}
        tabIndex={-1}
      >
        <div className="suchfenster__titel">
          <h2 id="suchfenster-titel">
            {springen ? 'Suchen' : `${ARTENNAME[festeArt ?? 'kunde']} auswählen`}
          </h2>
          <span className="suchfenster__hinweis">
            Die Felder ergänzen sich — jedes weitere verengt
          </span>
          <span className="taste" aria-hidden="true">
            Esc
          </span>
          {/* Auftrag CRM-102: das Kreuz für Maus und Finger, Esc bleibt für die Tastatur. */}
          <Schliesskreuz beiKlick={beiSchliessen} />
        </div>

        <div className="suchfenster__spalten">
          {springen && (
            <nav className="suchfenster__arten" aria-label="Arten">
              <p className="k">Arten</p>
              <button
                type="button"
                className={`suchfenster__art${art === 'alle' ? ' suchfenster__art--hier' : ''}`}
                onClick={() => setzeArt('alle')}
                aria-current={art === 'alle' ? 'true' : undefined}
              >
                <span>Alle</span>
                <span className="suchfenster__zahl">{verlauf.length}</span>
              </button>
              {arten.map((kandidat) => (
                <button
                  key={kandidat}
                  type="button"
                  className={`suchfenster__art${art === kandidat ? ' suchfenster__art--hier' : ''}`}
                  onClick={() => {
                    setzeArt(kandidat);
                    setzeFelder({});
                  }}
                  aria-current={art === kandidat ? 'true' : undefined}
                >
                  <span>{ARTENNAME[kandidat]}</span>
                  {/*
                    DIE TREFFERZAHL STEHT NUR AN DER GEWÄHLTEN ART. Solange
                    gefiltert wird, tragen die übrigen keine Zahl — ihre
                    Felder sind andere, eine Zahl dort wäre eine andere
                    Suche. Bei "Alle" zeigt jede Art, wie viele Einträge des
                    Verlaufs auf sie entfallen.
                  */}
                  {art === 'alle' ? (
                    <span className="suchfenster__zahl">
                      {verlauf.filter((e) => e.art === kandidat).length}
                    </span>
                  ) : art === kandidat && greift && ergebnis !== undefined ? (
                    <span className="suchfenster__zahl">{ergebnis.anzahl}</span>
                  ) : null}
                </button>
              ))}
            </nav>
          )}

          <div className="suchfenster__haupt">
            {art !== 'alle' && felderDerArt.length > 0 && (
              <div className="suchfenster__felder">
                <p className="k">
                  {ARTENNAME[art]} · die Felder ergänzen sich
                </p>
                <div className="suchfenster__feldreihe">
                  {felderDerArt.map((feld, stelle) => (
                    <Feld
                      key={feld.name}
                      feld={feld}
                      felder={felder}
                      setze={setze}
                      zuKurz={ergebnis?.zuKurz.includes(feld.name) === true}
                      unlesbar={unlesbar.includes(feld.name)}
                      ersteRef={stelle === 0 ? erstesFeld : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="suchfenster__leiste">
              <div className="umschalter" role="group" aria-label="Umfang">
                <label className="umschalter__wahl">
                  <input
                    type="radio"
                    name="suchfenster-umfang"
                    checked={nurEigene}
                    onChange={() => setzeNurEigene(true)}
                  />
                  <span>meine</span>
                </label>
                <label className="umschalter__wahl">
                  <input
                    type="radio"
                    name="suchfenster-umfang"
                    checked={!nurEigene}
                    onChange={() => setzeNurEigene(false)}
                  />
                  <span>alle</span>
                </label>
              </div>
              <span className="suchfenster__zaehlung" aria-live="polite">
                {zaehlung}
              </span>
              {zeigtVerlauf && verlauf.length > 0 && (
                <button
                  type="button"
                  className="knopf knopf--verweis"
                  onClick={() => {
                    void verlaufLoeschen().then(() => setzeVerlauf([]));
                  }}
                >
                  Verlauf löschen
                </button>
              )}
            </div>

            {ergebnis?.nichtVerfuegbar !== undefined ? (
              <p className="suchfenster__leer">{ergebnis.nichtVerfuegbar}</p>
            ) : fehler !== undefined ? (
              <p className="suchfenster__leer">{fehler}</p>
            ) : laeuft && zeigtVerlauf === false ? (
              /* EIN SKELETT, KEIN LADEBALKEN (M-07, NFA-01): Die Liste
                 behält ihre Höhe, und nichts springt, wenn die Treffer
                 eintreffen. */
              <ul className="suchfenster__liste" aria-busy="true">
                {[0, 1, 2, 3].map((i) => (
                  <li key={i} className="suchfenster__skelett">
                    <span />
                    <span />
                  </li>
                ))}
              </ul>
            ) : zeilen.length === 0 ? (
              <Leermeldung
                art={art}
                greift={greift}
                springen={springen}
                zuKurz={ergebnis?.zuKurz ?? []}
              />
            ) : (
              <ul className="suchfenster__liste" role="listbox" aria-label="Treffer">
                {zeilen.map((zeile, stelle) => (
                  <li key={`${zeile.art}:${zeile.id}`}>
                    <button
                      type="button"
                      className={`suchfenster__treffer${
                        stelle === markiert ? ' suchfenster__treffer--hier' : ''
                      }`}
                      role="option"
                      aria-selected={stelle === markiert}
                      onMouseEnter={() => setzeMarkiert(stelle)}
                      onClick={() => waehle(zeile)}
                    >
                      <span className="suchfenster__wer">
                        <b>{zeile.bezeichnung}</b>
                        <small>
                          {art === 'alle' ? `${ARTENNAME[zeile.art]} · ` : ''}
                          {zeile.zusatz}
                        </small>
                      </span>
                      {zeile.wann !== undefined && (
                        <span className="suchfenster__wann">{zeile.wann}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="suchfenster__fuss">
              <span className="suchfenster__hinweis">
                <span className="taste">↑</span> <span className="taste">↓</span> wählen ·{' '}
                <span className="taste">⏎</span> {springen ? 'öffnen' : 'übernehmen'} ·{' '}
                <span className="taste">Tab</span> zwischen den Feldern · ein Feld leeren erweitert
                wieder
              </span>
              {/*
                ZWEI AUSGÄNGE BEIM SPRINGEN, EINER BEIM AUSWÄHLEN. "Alle
                Treffer anzeigen" würde im Auswahl-Betrieb den Beleg
                verlassen, an dem gerade gearbeitet wird.
              */}
              {springen && beiAlleTreffer !== undefined && art !== 'alle' && (
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => beiAlleTreffer(art, felder)}
                >
                  Alle Treffer anzeigen →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Die Leermeldung. Ein Satz im Klartext, und er nennt, WORAN es liegt —
 * "keine Treffer" allein lässt den Benutzer raten, welches Feld zu eng war.
 */
function Leermeldung({
  art,
  greift,
  springen,
  zuKurz,
}: {
  readonly art: Suchart | 'alle';
  readonly greift: boolean;
  readonly springen: boolean;
  readonly zuKurz: readonly string[];
}): JSX.Element {
  if (zuKurz.length > 0) {
    return (
      <p className="suchfenster__leer">
        {zuKurz.join(' und ')} {zuKurz.length === 1 ? 'braucht' : 'brauchen'} mindestens drei
        Zeichen. Bis dahin steht der Verlauf.
      </p>
    );
  }
  if (!greift) {
    return (
      <p className="suchfenster__leer">
        {art === 'alle'
          ? 'Noch nichts geöffnet. Wählen Sie links eine Art und füllen Sie ein Feld — die Felder ergänzen sich.'
          : 'Hier war noch nichts. Füllen Sie eines der Felder darüber.'}
      </p>
    );
  }
  return (
    <p className="suchfenster__leer">
      Kein Treffer. Leeren Sie ein Feld — jedes erweitert die Liste wieder.
      {springen ? '' : ' Fehlt der Datensatz, wird er in seiner eigenen Maske angelegt.'}
    </p>
  );
}

/** Ein benanntes Feld. Vier Bauarten, eine Beschriftung. */
function Feld({
  feld,
  felder,
  setze,
  zuKurz,
  unlesbar,
  ersteRef,
}: {
  readonly feld: Felddefinition;
  readonly felder: Felder;
  readonly setze: (schluessel: keyof Felder, wert: string) => void;
  readonly zuKurz: boolean;
  /** Ein Betrag, der so nicht zu lesen ist — die Meldung steht am Feld. */
  readonly unlesbar: boolean;
  readonly ersteRef?: React.RefObject<HTMLInputElement | null> | undefined;
}): JSX.Element {
  const kennung = `suchfenster-${feld.schluessel}`;

  if (feld.art === 'zeitraum') {
    return (
      <div className="feld suchfenster__feld">
        <span className="feld__beschriftung">{feld.name}</span>
        <div className="suchfenster__spanne">
          <input
            type="date"
            aria-label={`${feld.name} von`}
            value={felder.von ?? ''}
            onChange={(e) => setze('von', e.currentTarget.value)}
          />
          <span aria-hidden="true">–</span>
          <input
            type="date"
            aria-label={`${feld.name} bis`}
            value={felder.bis ?? ''}
            onChange={(e) => setze('bis', e.currentTarget.value)}
          />
        </div>
      </div>
    );
  }

  if (feld.art === 'betrag') {
    return (
      <div className={`feld suchfenster__feld${unlesbar ? ' feld--fehlerhaft' : ''}`}>
        <span className="feld__beschriftung">{feld.name}</span>
        <div className="suchfenster__spanne">
          <input
            type="text"
            inputMode="decimal"
            aria-label={`${feld.name} ab`}
            placeholder="ab"
            value={felder.betragVon ?? ''}
            onChange={(e) => setze('betragVon', e.currentTarget.value)}
          />
          <span aria-hidden="true">–</span>
          <input
            type="text"
            inputMode="decimal"
            aria-label={`${feld.name} bis`}
            placeholder="bis"
            value={felder.betragBis ?? ''}
            onChange={(e) => setze('betragBis', e.currentTarget.value)}
          />
        </div>
        {unlesbar && (
          <small className="feld__fehler">
            Betrag als Zahl, mit Komma oder Punkt — ohne Tausenderzeichen. Beispiel: 1264,80
          </small>
        )}
      </div>
    );
  }

  if (feld.art === 'datum') {
    return (
      <div className="feld suchfenster__feld">
        <label className="feld__beschriftung" htmlFor={kennung}>
          {feld.name}
        </label>
        <input
          id={kennung}
          type="date"
          value={felder[feld.schluessel] ?? ''}
          onChange={(e) => setze(feld.schluessel, e.currentTarget.value)}
        />
      </div>
    );
  }

  if (feld.art === 'auswahl') {
    return (
      <div className="feld suchfenster__feld">
        <label className="feld__beschriftung" htmlFor={kennung}>
          {feld.name}
        </label>
        <select
          id={kennung}
          value={felder[feld.schluessel] ?? ''}
          onChange={(e) => setze(feld.schluessel, e.currentTarget.value)}
        >
          <option value="">Alle</option>
          {(feld.auswahl ?? []).map((wahl) => (
            <option key={wahl.wert} value={wahl.wert}>
              {wahl.text}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="feld suchfenster__feld">
      <label className="feld__beschriftung" htmlFor={kennung}>
        {feld.name}
      </label>
      <input
        id={kennung}
        ref={ersteRef}
        type="text"
        className="feld__eingabe"
        autoComplete="off"
        value={felder[feld.schluessel] ?? ''}
        onChange={(e) => setze(feld.schluessel, e.currentTarget.value)}
        aria-describedby={zuKurz ? `${kennung}-hinweis` : undefined}
      />
      {zuKurz && (
        <small className="feld__hilfe" id={`${kennung}-hinweis`}>
          Noch mindestens drei Zeichen
        </small>
      )}
    </div>
  );
}
